import styles from "@/styles/BasketCell.module.css";
import { Castle, Game, Other, Tent, Darts, Database } from "./Database";
import { useRouter } from "next/router";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type BasketCellProps = {
  type: string;
  content: Castle | Game | Other | Tent | Darts;
  contentType: string;
  amount?: string;
  database: Database;
  promotionVariable: boolean | null;
};

export default function BasketCell({
  type,
  content,
  contentType,
  database,
  promotionVariable,
}: BasketCellProps) {
  const router = useRouter();
  const nl = router.asPath.includes("/nl/");
  const fr = router.asPath.includes("/fr/");
  const castle = contentType === "castle";
  const game = contentType === "game";
  const other = contentType === "other";
  const tent = contentType === "tent";
  const darts = contentType === "darts";

  const [amount, setAmount] = useState<string | null>(null);

  const amountRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const newAmount = sessionStorage.getItem(content.nameNL);
      if (newAmount !== amount) {
        setAmount(newAmount);
        if (amountRef.current && newAmount) {
          amountRef.current.value = newAmount;
        }
      }
    }, 100);
    return () => clearInterval(intervalId);
  }, [amount, content.nameNL]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        amountRef.current &&
        !amountRef.current.contains(event.target as Node) &&
        amountRef.current.value === ""
      ) {
        amountRef.current.value = "1";
        sessionStorage.setItem(content.nameNL, "1");
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [content.nameNL]);

  function removeFromBasket() {
    if (sessionStorage.getItem("firstChoiceNL") === content.nameNL) {
      sessionStorage.removeItem("firstChoiceNL");
      sessionStorage.removeItem("firstChoiceFR");
    }
    if (sessionStorage.getItem("secondChoiceNL") === content.nameNL) {
      sessionStorage.removeItem("secondChoiceNL");
      sessionStorage.removeItem("secondChoiceFR");
    }
    const combined = [...database.castles, ...database.games];
    if (combined.map((castle) => castle.nameNL).includes(content.nameNL)) {
      const resultingCastles = (sessionStorage.getItem("multipleCastles") || "")
        .split(",")
        .filter((castle) => castle !== content.nameNL)
        .join(",");
      if (resultingCastles === "") {
        sessionStorage.removeItem("multipleCastles");
      } else {
        sessionStorage.setItem("multipleCastles", resultingCastles);
      }
    }
    if (database.other.map((other) => other.nameNL).includes(content.nameNL)) {
      const resultingRequests = (sessionStorage.getItem("Requests") || "")
        .split(",")
        .filter((request) => request !== content.nameNL)
        .join(",");
      if (resultingRequests === "") {
        sessionStorage.removeItem("Requests");
      } else {
        sessionStorage.setItem("Requests", resultingRequests);
      }
    }
    if (database.tent.map((tent) => tent.nameNL).includes(content.nameNL)) {
      const resultingRequests = (sessionStorage.getItem("Requests") || "")
        .split(",")
        .filter((request) => request !== content.nameNL)
        .join(",");
      if (resultingRequests === "") {
        sessionStorage.removeItem("Requests");
      } else {
        sessionStorage.setItem("Requests", resultingRequests);
      }
      sessionStorage.removeItem(content.nameNL);
    }
    if (database.darts.map((darts) => darts.nameNL).includes(content.nameNL)) {
      const resultingRequests = (sessionStorage.getItem("Requests") || "")
        .split(",")
        .filter((request) => request !== content.nameNL)
        .join(",");
      if (resultingRequests === "") {
        sessionStorage.removeItem("Requests");
      } else {
        sessionStorage.setItem("Requests", resultingRequests);
      }
      sessionStorage.removeItem(content.nameNL);
    }
  }

  function amountChanged(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.value.includes("-") || event.target.value === "0") {
      sessionStorage.setItem(content.nameNL, "1");
    } else {
      sessionStorage.setItem(content.nameNL, event.target.value);
    }
  }

  if (type == "basket") {
    return (
      <div className={styles.cell}>
        <button onClick={() => removeFromBasket()}>
          <X />
        </button>
        <div className={styles.image}>
          {nl && <img src={content.imagePath} width={80} height={80} alt="Foto van artikel" />}
          {fr && <img src={content.imagePath} width={80} height={80} alt="Photo de l'article" />}
        </div>
        {nl && <p className={styles.name}>{content.nameNL}</p>}
        {fr && <p className={styles.name}>{content.nameFR}</p>}
        <div className={styles.priceAndPromo}>
          {(castle || game) && nl && <p>&euro;{(content as Castle).price} / dag*</p>}
          {(castle || game) && fr && <p>&euro;{(content as Castle).price} / jour*</p>}
          {tent && <p>&euro;{(content as Tent).price} / weekend*</p>}
          {darts && nl && <p>&euro;{(content as Darts).price} / dag*</p>}
          {darts && fr && <p>&euro;{(content as Darts).price} / jour*</p>}
          {promotionVariable !== null && promotionVariable && (castle || game) && nl && (
            <>
              <div className={styles.oldPrice}>
                <p className={styles.scratchedPrice}>&euro;{(content as Castle).promotion}</p>
                <p className={styles.promoDays}>/ 2 dagen*</p>
              </div>
              <div className={styles.specialPromo}>
                <p className={styles.promo}>&euro;{(content as Castle).price}</p>
                <p className={styles.promoText}>PROMO</p>
              </div>
            </>
          )}
          {promotionVariable !== null && !promotionVariable && (castle || game) && nl && (
            <p className={styles.promo}>&euro;{(content as Castle).promotion} / 2 dagen*</p>
          )}
          {promotionVariable !== null && promotionVariable && (castle || game) && fr && (
            <>
              <div className={styles.oldPrice}>
                <p className={styles.scratchedPrice}>&euro;{(content as Castle).promotion}</p>
                <p className={styles.promoDays}>/ 2 jours</p>
              </div>
              <div className={styles.specialPromo}>
                <p className={styles.promo}>&euro;{(content as Castle).price}</p>
                <p className={styles.promoText}>PROMO</p>
              </div>
            </>
          )}
          {promotionVariable !== null && !promotionVariable && (castle || game) && fr && (
            <p className={styles.promo}>&euro;{(content as Castle).promotion} / 2 jours*</p>
          )}
        </div>
      </div>
    );
  } else if (type == "reserve") {
    return (
      <div className={styles.cellBig}>
        <button onClick={() => removeFromBasket()}>
          <X />
        </button>
        <div className={styles.image}>
          {nl && <img src={content.imagePath} width={80} height={80} alt="Foto van artikel" />}
          {fr && <img src={content.imagePath} width={80} height={80} alt="Photo de l'article" />}
        </div>
        <div className={styles.nameAndAmount}>
          {nl && <p className={styles.name}>{content.nameNL}</p>}
          {fr && <p className={styles.name}>{content.nameFR}</p>}
          {tent && nl && (
            <input
              id={`amountInput-${content.imagePath}`}
              className={styles.amountInput}
              type="number"
              placeholder="Aantal"
              defaultValue={amount || "1"}
              min="1"
              onChange={amountChanged}
              ref={amountRef}
            />
          )}
          {tent && fr && (
            <input
              id={`amountInput-${content.imagePath}`}
              className={styles.amountInput}
              type="number"
              placeholder="Nombre"
              defaultValue={amount || "1"}
              min="1"
              onChange={amountChanged}
              ref={amountRef}
            />
          )}
        </div>
        <div className={styles.priceAndPromo}>
          {(castle || game) && nl && <p>&euro;{(content as Castle).price} / dag*</p>}
          {(castle || game) && fr && <p>&euro;{(content as Castle).price} / jour*</p>}
          {tent && <p>&euro;{(content as Tent).price} / weekend*</p>}
          {darts && nl && <p>&euro;{(content as Darts).price} / dag*</p>}
          {darts && fr && <p>&euro;{(content as Darts).price} / jour*</p>}
          {promotionVariable !== null && promotionVariable && (castle || game) && nl && (
            <>
              <div className={styles.oldPrice}>
                <p className={styles.scratchedPrice}>&euro;{(content as Castle).promotion}</p>
                <p className={styles.promoDays}>/ 2 dagen*</p>
              </div>
              <div className={styles.specialPromo}>
                <p className={styles.promo}>&euro;{(content as Castle).price}</p>
                <p className={styles.promoText}>PROMO</p>
              </div>
            </>
          )}
          {promotionVariable !== null && !promotionVariable && (castle || game) && nl && (
            <p className={styles.promo}>&euro;{(content as Castle).promotion} / 2 dagen*</p>
          )}
          {promotionVariable !== null && promotionVariable && (castle || game) && fr && (
            <>
              <div className={styles.oldPrice}>
                <p className={styles.scratchedPrice}>&euro;{(content as Castle).promotion}</p>
                <p className={styles.promoDays}>/ 2 jours*</p>
              </div>
              <div className={styles.specialPromo}>
                <p className={styles.promo}>&euro;{(content as Castle).price}</p>
                <p className={styles.promoText}>PROMO</p>
              </div>
            </>
          )}
          {promotionVariable !== null && !promotionVariable && (castle || game) && fr && (
            <p className={styles.promo}>&euro;{(content as Castle).promotion} / 2 jours*</p>
          )}
        </div>
      </div>
    );
  }
}
